import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, View, Text, Pressable, Platform } from 'react-native';
import { Minus, Plus, Lock, Zap, BarChart2, List } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { SoccerKit } from '../../ui/SoccerKit';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
// The WC card's purple wave background — self-contained, reused for design parity
// with the WC picks screen (user directive: take the WC design style).
import { MatchCardHeroShell } from '../../worldcup/MatchesView/MatchCardHeroShell';
import { getFinishedScoreDisplay } from '../../worldcup/MatchesView/matchCardFinishedScore.helper';
import type { ApiMatch } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import {
  resolveSeasonPickOutcome,
  isScoredOutcome,
} from '../../../src/competitions/helpers/seasonPickOutcome.helper';
import {
  SEASON_PICKS_MAX_SCORE,
  SEASON_PICKS_SAVE_ERROR,
  SEASON_PICKS_YOUR_PICK_PREFIX,
  SEASON_PICKS_LOCKS_AT_KICKOFF,
  SEASON_PICKS_OUTCOME_LABEL,
  SEASON_PICKS_SCORING,
  SEASON_PICKS_NO_PICK,
  SEASON_PICKS_BOOST_ON,
  SEASON_PICKS_BOOST_OFF,
  SEASON_PICKS_BOOST_TAKEN,
  SEASON_PICKS_BOOSTED_MARK,
  SEASON_PICKS_BOOSTED_A11Y,
  SEASON_PICKS_ACTION_STATS,
  SEASON_PICKS_ACTION_FIXTURE,
  SEASON_PICKS_TEST_IDS,
  liveMinuteLabel,
} from './SeasonPicks.constants';
import { getSeasonPickCardStyles } from './SeasonPicks.styles';

export interface SeasonPickValue {
  home: number;
  away: number;
}

/**
 * Golden Ball control (specs/champions-league AC-4.5). Absent for season pools, which have no
 * boost, so the season card is byte-identical to before.
 */
export interface SeasonPickBoost {
  /** Render the control at all. False = this stage takes no x2. */
  eligible: boolean;
  /** This match currently holds the user's boost for its slot. */
  active: boolean;
  /** Tappable. False renders it dimmed rather than hiding it, so the rule stays visible. */
  placeable: boolean;
  /**
   * Another match in this match's round already holds the boost (AC-12.1). Dims the control
   * exactly as `placeable: false` does: one x2 per round is a rule, not a timing accident, and
   * the card should say so the same way either way.
   */
  lockedByOther?: boolean;
  onToggle: (match: ApiMatch) => void;
}

interface SeasonMatchPickCardProps {
  match: ApiMatch;
  /** Effective pick shown (local override merged over server prediction); null = no pick yet. */
  pick: SeasonPickValue | null;
  hasSaveError: boolean;
  onChangePick: (matchId: string, home: number, away: number) => void;
  boost?: SeasonPickBoost | null;
  /**
   * Card action row (specs/champions-league AC-10.1). Both optional: a season pool passes
   * neither and its card keeps exactly the shape it had before Story 10 (AC-10.8).
   */
  onStatsPress?: (matchId: string) => void;
  onFixturePress?: (matchId: string) => void;
  /**
   * The tie's aggregate, oriented to THIS fixture's home/away (specs/champions-league AC-6.9).
   * Only ever set on a second leg — there is nothing to aggregate on the opening one, and a
   * single-legged tie would only repeat the score above it. A season pool passes nothing.
   */
  aggregate?: { home: number; away: number } | null;
}

function formatKickoff(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const clamp = (n: number) => Math.max(0, Math.min(SEASON_PICKS_MAX_SCORE, n));

export const SeasonMatchPickCard: React.FC<SeasonMatchPickCardProps> = ({
  match, pick, hasSaveError, onChangePick, boost = null,
  onStatsPress, onFixturePress, aggregate = null,
}) => {
  const theme = useTheme();
  const s = useMemo(() => getSeasonPickCardStyles(theme), [theme]);

  const editable = (match.isPickEditable ?? match.status === 'upcoming') && match.status === 'upcoming';
  const settled = match.prediction?.pointsEarned != null;
  // Slice 4: the card says HOW the pick did, not just what it scored. Classification
  // is derived locally; the points value stays whatever the server settled.
  const outcome = resolveSeasonPickOutcome(match);
  const scored = isScoredOutcome(outcome);
  const accentBarStyle = outcome === 'exact'
    ? s.accentBarExact
    : outcome === 'result' ? s.accentBarResult : s.accentBarMiss;
  const outcomeLabelStyle = outcome === 'exact'
    ? s.outcomeLabelExact
    : outcome === 'result' ? s.outcomeLabelResult : s.outcomeLabelMiss;
  const pointsStyle = outcome === 'exact'
    ? s.pointsExact
    : outcome === 'result' ? s.pointsResult : s.pointsMiss;
  const homeKit = useMemo(() => getTeamKitConfig(match.homeTeam), [match.homeTeam]);
  const awayKit = useMemo(() => getTeamKitConfig(match.awayTeam), [match.awayTeam]);

  const step = (side: 'home' | 'away', delta: number) => {
    const base = pick ?? { home: 0, away: 0 };
    const next = {
      home: side === 'home' ? clamp(base.home + delta) : base.home,
      away: side === 'away' ? clamp(base.away + delta) : base.away,
    };
    onChangePick(match.matchId, next.home, next.away);
  };

  // A tie decided past 90 minutes has TWO scores, and the card must not show one beside a
  // verdict computed from the other (AC-14.6): a 0-1 pick on a tie that finished 0-1 at 90
  // minutes and 2-1 in extra time read "2 : 1" with an EXACT badge. `resolveSeasonPickOutcome`
  // judges on `fullTime*`, so the headline stays the final and the 90-minute line is stated
  // underneath — the treatment the World Cup card already has, reused rather than re-derived.
  //
  // For a fixture that never went past 90 (every league-phase game) this is `single` and the
  // card is exactly as it was. A sport that settles on overtime rather than regulation
  // (basketball: `settlesOnRegulation: false`) never populates `fullTime*`, so it is `single`
  // too and can never grow a second line.
  const finishedScore = useMemo(() => getFinishedScoreDisplay(match), [match]);
  const scoreLabel = editable
    ? (pick ? `${pick.home} : ${pick.away}` : '– : –')
    : (match.homeScore != null && match.awayScore != null
      ? `${match.homeScore} : ${match.awayScore}`
      : '– : –');
  const beyondNinety = !editable && finishedScore.kind === 'extraTime' ? finishedScore : null;

  const steppers = (side: 'home' | 'away', teamName: string) => (
    <View style={s.steppersRow}>
      <Pressable
        style={({ pressed }) => [s.stepperButton, pressed && s.stepperButtonPressed]}
        onPress={() => step(side, -1)}
        testID={SEASON_PICKS_TEST_IDS.stepper(match.matchId, side, 'minus')}
        accessibilityRole="button"
        accessibilityLabel={`${teamName} minus`}
      >
        <Minus size={16} color={theme.gray[400]} />
      </Pressable>
      <Pressable
        style={({ pressed }) => [s.stepperButton, s.stepperButtonPlus, pressed && s.stepperButtonPressed]}
        onPress={() => step(side, 1)}
        testID={SEASON_PICKS_TEST_IDS.stepper(match.matchId, side, 'plus')}
        accessibilityRole="button"
        accessibilityLabel={`${teamName} plus`}
      >
        <Plus size={16} color={theme.primaryLight} />
      </Pressable>
    </View>
  );

  const pickIsSet = editable && pick != null;
  // Both halves come or go together: the row always renders both buttons, so with `||` a
  // caller supplying one handler would get a visible, inert second button beside a divider.
  const showActions = onStatsPress != null && onFixturePress != null;

  // A boost is tappable when the match can still take one AND this round has not already
  // spent its x2 elsewhere. The holder itself is never `lockedByOther`, so it stays tappable
  // and a mis-placed boost can always be cleared (AC-12.2).
  const canPlaceBoost = boost != null && boost.placeable && !boost.lockedByOther;
  const boostAccessibilityLabel = boost?.active
    ? SEASON_PICKS_BOOST_ON
    : boost?.lockedByOther
      ? SEASON_PICKS_BOOST_TAKEN
      : SEASON_PICKS_BOOST_OFF;

  const isLive = match.status === 'live';
  const liveAnim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (!isLive) return undefined;
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(liveAnim, { toValue: 0.2, duration: 700, useNativeDriver: true }),
        Animated.timing(liveAnim, { toValue: 1, duration: 700, useNativeDriver: true }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [isLive, liveAnim]);

  // A finished match cannot take a boost, so the control is pure noise there. The doubling is
  // reported instead beside the points, which is where the number it doubled lives (AC-14.4).
  const wasBoosted = match.status === 'finished' && match.hasGoldenBall;
  const showBoostControl = boost?.eligible === true && match.status !== 'finished';

  return (
    <View
      style={[s.card, pickIsSet && s.cardPicked, !editable && s.cardLocked]}
      testID={SEASON_PICKS_TEST_IDS.card(match.matchId)}
    >
      <MatchCardHeroShell />
      {scored && <View style={[s.accentBar, accentBarStyle]} />}
      <View style={s.cardContent}>
        {/* Leading / centre / trailing, so the x2 keeps one place on the card however many
            status badges its neighbours happen to be showing (AC-16.5). */}
        <View style={s.metaRow}>
          <View style={s.metaLeading} testID={SEASON_PICKS_TEST_IDS.metaLeading(match.matchId)}>
            {isLive ? (
              <View
                style={[s.stageBadge, s.liveBadge]}
                testID={SEASON_PICKS_TEST_IDS.live(match.matchId)}
              >
                <Animated.View style={[s.liveDot, { opacity: liveAnim }]} />
                <Text style={s.liveText}>{liveMinuteLabel(match.liveMinute)}</Text>
              </View>
            ) : (
              <View style={s.stageBadge}>
                {!editable && <Lock size={11} color={theme.white} />}
                <Text style={s.stageBadgeText} numberOfLines={1}>
                  {formatKickoff(match.kickoffTime)}
                </Text>
              </View>
            )}
          </View>

          <View style={s.metaCenter} testID={SEASON_PICKS_TEST_IDS.metaCenter(match.matchId)}>
            {showBoostControl && boost != null && (
              <Pressable
                disabled={!canPlaceBoost}
                onPress={() => boost.onToggle(match)}
                style={({ pressed }) => [
                  s.boostButton,
                  boost.active && s.boostButtonActive,
                  // `!boost.active` matters once the boosted match kicks off (AC-16.11): the
                  // fill answers "is this mine?", tappability answers "can I move it?", and
                  // without the guard the disabled treatment overrode the held one — so a
                  // player checking where their x2 went saw it greyed out like a slot spent
                  // elsewhere. The label and the icon below already read `active` first.
                  !boost.active && !canPlaceBoost && s.boostButtonDisabled,
                  pressed && canPlaceBoost && s.boostButtonPressed,
                ]}
                testID={SEASON_PICKS_TEST_IDS.boost(match.matchId)}
                accessibilityRole="button"
                accessibilityState={{ selected: boost.active, disabled: !canPlaceBoost }}
                accessibilityLabel={boostAccessibilityLabel}
              >
                <Zap
                  size={9}
                  color={
                    boost.active
                      ? theme.textOnCard
                      : canPlaceBoost ? theme.accent : theme.gray[400]
                  }
                  fill={boost.active ? theme.textOnCard : 'transparent'}
                  strokeWidth={2.5}
                />
                <Text
                  style={[
                    s.boostLabel,
                    boost.active && s.boostLabelActive,
                    !boost.active && !canPlaceBoost && s.boostLabelDisabled,
                  ]}
                >
                  ×2
                </Text>
              </Pressable>
            )}
          </View>

          <View style={s.metaTrailing} testID={SEASON_PICKS_TEST_IDS.metaTrailing(match.matchId)}>
            {hasSaveError && (
              <View style={s.stageBadge}>
                <Text style={s.saveError}>{SEASON_PICKS_SAVE_ERROR}</Text>
              </View>
            )}
            {/* `scored`, not `settled`: a finished match can carry a settled pointsEarned
                while its scoreline has not synced yet, which classifies as 'pending'.
                Gating on `settled` rendered BOTH this badge and "Scoring…" side by side. */}
            {!hasSaveError && scored && (
              <View style={s.stageBadge}>
                {wasBoosted && (
                  <>
                    <Zap size={10} color={theme.accent} fill={theme.accent} strokeWidth={2.5} />
                    <Text
                      style={s.boostedMark}
                      testID={SEASON_PICKS_TEST_IDS.boostedMark(match.matchId)}
                      accessibilityLabel={SEASON_PICKS_BOOSTED_A11Y}
                    >
                      {SEASON_PICKS_BOOSTED_MARK}
                    </Text>
                  </>
                )}
                <Text style={[s.pointsText, pointsStyle]}>
                  {`+${match.prediction?.pointsEarned} pts`}
                </Text>
              </View>
            )}
            {!hasSaveError && outcome === 'pending' && (
              <View style={s.stageBadge}>
                <Text style={s.pointsPending}>{SEASON_PICKS_SCORING}</Text>
              </View>
            )}
            {!hasSaveError && !settled && editable && (
              <View style={s.stageBadge}>
                <Text style={s.stageBadgeText} numberOfLines={1}>
                  {SEASON_PICKS_LOCKS_AT_KICKOFF}
                </Text>
              </View>
            )}
          </View>
        </View>

      <View style={s.mainRow}>
        <View style={s.sideCol}>
          <SoccerKit primary={homeKit.primary} secondary={homeKit.secondary} type={homeKit.type} size={40} />
          <Text style={[s.teamName, !editable && !isLive && s.teamNameLocked]} numberOfLines={2}>
            {match.homeTeam}
          </Text>
          {editable && steppers('home', match.homeTeam)}
        </View>

        <View style={s.centerCol}>
          <View style={[s.scoreBox, editable && !pick && s.scoreBoxEmpty, editable && pick != null && s.scoreBoxPicked]}>
            <Text style={[s.scoreText, editable && !pick && s.scoreTextEmpty]}>{scoreLabel}</Text>
          </View>
          {aggregate && (
            <Text style={s.aggregateLine} testID={SEASON_PICKS_TEST_IDS.aggregate(match.matchId)}>
              {`agg. ${aggregate.home}–${aggregate.away}`}
            </Text>
          )}
          {beyondNinety && (
            <View style={s.beyondNinetyBlock} testID={SEASON_PICKS_TEST_IDS.beyondNinety(match.matchId)}>
              {beyondNinety.penalty && (
                <Text style={s.beyondNinetyPens}>
                  {`${beyondNinety.penalty.home}–${beyondNinety.penalty.away} pens`}
                </Text>
              )}
              <Text style={s.beyondNinetyRegulation}>
                {`90 min ${beyondNinety.regulation.home}–${beyondNinety.regulation.away}`}
              </Text>
            </View>
          )}
        </View>

        <View style={s.sideCol}>
          <SoccerKit primary={awayKit.primary} secondary={awayKit.secondary} type={awayKit.type} size={40} />
          <Text style={[s.teamName, !editable && !isLive && s.teamNameLocked]} numberOfLines={2}>
            {match.awayTeam}
          </Text>
          {editable && steppers('away', match.awayTeam)}
        </View>
      </View>

        {!editable && match.prediction && (
          <View style={s.settledFooter}>
            <Text style={s.yourPickLine}>
              {`${SEASON_PICKS_YOUR_PICK_PREFIX} ${match.prediction.home} : ${match.prediction.away}`}
            </Text>
            {scored && (
              <Text style={[s.outcomeLabel, outcomeLabelStyle]}>
                {SEASON_PICKS_OUTCOME_LABEL[outcome]}
              </Text>
            )}
          </View>
        )}

        {outcome === 'no-pick' && (
          <Text style={s.noPickLine}>{SEASON_PICKS_NO_PICK}</Text>
        )}
      </View>

      {/* Last child of the card, deliberately outside `cardContent` and outside every
          state branch: the row spans the full width and is present on an editable,
          locked, scoring, settled and no-pick card alike (AC-10.2). */}
      {showActions && (
        <View style={s.actionRow}>
          <Pressable
            onPress={() => onStatsPress?.(match.matchId)}
            style={({ pressed }) => [
              s.actionButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && s.actionButtonPressed,
            ]}
            testID={SEASON_PICKS_TEST_IDS.stats(match.matchId)}
            accessibilityRole="button"
            accessibilityLabel={SEASON_PICKS_ACTION_STATS}
          >
            <BarChart2 size={13} color={s.actionIcon.color as string} strokeWidth={2} />
            <Text style={s.actionText}>{SEASON_PICKS_ACTION_STATS}</Text>
          </Pressable>

          <View style={s.actionDivider} />

          <Pressable
            onPress={() => onFixturePress?.(match.matchId)}
            style={({ pressed }) => [
              s.actionButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && s.actionButtonPressed,
            ]}
            testID={SEASON_PICKS_TEST_IDS.fixture(match.matchId)}
            accessibilityRole="button"
            accessibilityLabel={SEASON_PICKS_ACTION_FIXTURE}
          >
            <List size={13} color={s.actionIcon.color as string} strokeWidth={2} />
            <Text style={s.actionText}>{SEASON_PICKS_ACTION_FIXTURE}</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
};
